import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useQuery, type QueryObserverResult, type RefetchOptions } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { useLocation } from "@tanstack/react-router";
import { useAppNavigate, type AppNavigateOptions } from "@/lib/use-app-navigate";
import { api } from "@constructionautomation/backend";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";

const LAST_PROJECT_KEY = "construction-cortex:last-project-id";
const RECENT_PROJECTS_KEY = "construction-cortex:recent-project-ids";
const MAX_RECENT_PROJECTS = 3;

/**
 * Pages that work without a selected project. A new company has no projects (and, before a plan
 * or grant, cannot create one), so it must still reach company settings and billing.
 */
export function isProjectIndependentPath(pathname: string) {
  return (
    pathname === "/" ||
    pathname === "/organization" ||
    pathname === "/training" ||
    pathname === "/inbox" ||
    pathname === "/admin" ||
    pathname.startsWith("/admin/")
  );
}

interface ProjectContextValue {
  projectId: Id<"projects"> | null;
  project: Project | null;
  projects: Project[] | undefined;
  recentProjectIds: Id<"projects">[];
  isLoading: boolean;
  error: Error | null;
  setProjectId: (projectId: Id<"projects"> | null) => void;
  switchProject: (projectId: Id<"projects">, navigateTo?: AppNavigateOptions) => void;
  refreshProjects: (
    options?: RefetchOptions
  ) => Promise<QueryObserverResult<Project[], Error>>;
}

interface Project {
  _id: Id<"projects">;
  _creationTime: number;
  organizationId?: Id<"organizations">;
  name: string;
  itemCount?: number;
  documentCount?: number;
  createdAt: number;
}

const ProjectContext = createContext<ProjectContextValue | null>(null);

function getLastProjectIdFromStorage(): Id<"projects"> | null {
  try {
    const stored = localStorage.getItem(LAST_PROJECT_KEY);
    return stored ? (stored as Id<"projects">) : null;
  } catch {
    return null;
  }
}

function setLastProjectIdInStorage(projectId: Id<"projects"> | null): void {
  try {
    if (projectId) {
      localStorage.setItem(LAST_PROJECT_KEY, projectId);
    } else {
      localStorage.removeItem(LAST_PROJECT_KEY);
    }
  } catch {
    // Ignore storage errors
  }
}

function getRecentProjectIdsFromStorage(): Id<"projects">[] {
  try {
    const stored = localStorage.getItem(RECENT_PROJECTS_KEY);
    if (!stored) {
      return [];
    }

    const parsed = JSON.parse(stored);
    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed.filter((value): value is Id<"projects"> => typeof value === "string");
  } catch {
    return [];
  }
}

function setRecentProjectIdsInStorage(projectIds: Id<"projects">[]): void {
  try {
    if (projectIds.length === 0) {
      localStorage.removeItem(RECENT_PROJECTS_KEY);
      return;
    }

    localStorage.setItem(RECENT_PROJECTS_KEY, JSON.stringify(projectIds));
  } catch {
    // Ignore storage errors
  }
}

function addRecentProjectId(
  projectIds: Id<"projects">[],
  projectId: Id<"projects">
): Id<"projects">[] {
  return [projectId, ...projectIds.filter((id) => id !== projectId)].slice(
    0,
    MAX_RECENT_PROJECTS
  );
}

interface ProjectProviderProps {
  children: ReactNode;
}

export function ProjectProvider({ children }: ProjectProviderProps) {
  const navigate = useAppNavigate();
  const location = useLocation();
  const [projectId, setProjectIdState] = useState<Id<"projects"> | null>(null);
  const [recentProjectIds, setRecentProjectIdsState] = useState<Id<"projects">[]>(() =>
    getRecentProjectIdsFromStorage()
  );
  const [isInitialized, setIsInitialized] = useState(false);

  // Fetch all projects
  const {
    data: projects,
    isLoading: isProjectsLoading,
    error: projectsError,
    refetch: refreshProjects,
  } = useQuery(convexQuery(api.projects.listAll, {}));

  // Fetch current project details
  const {
    data: project,
    isLoading: isProjectLoading,
    error: projectError,
  } = useQuery(convexQuery(api.projects.getById, projectId ? { projectId } : "skip"));

  // Initialize project from localStorage or auto-select first project
  useEffect(() => {
    if (isInitialized || isProjectsLoading || !projects) return;

    if (projects.length === 0) {
      // No projects - will redirect to home
      if (recentProjectIds.length > 0) {
        setRecentProjectIdsState([]);
        setRecentProjectIdsInStorage([]);
      }
      setIsInitialized(true);
      return;
    }

    const lastProjectId = getLastProjectIdFromStorage();
    
    if (lastProjectId) {
      // Check if last project still exists and user has access
      const lastProjectExists = projects.some((p) => p._id === lastProjectId);
      if (lastProjectExists) {
        setProjectIdState(lastProjectId);
        const nextRecentProjectIds = addRecentProjectId(recentProjectIds, lastProjectId);
        setRecentProjectIdsState(nextRecentProjectIds);
        setRecentProjectIdsInStorage(nextRecentProjectIds);
        setIsInitialized(true);
        return;
      }
      // Clear stale project ID from storage since it no longer exists
      setLastProjectIdInStorage(null);
    }

    // Auto-select first project
    const firstProject = projects[0];
    if (firstProject) {
      setProjectIdState(firstProject._id);
      setLastProjectIdInStorage(firstProject._id);
      const nextRecentProjectIds = addRecentProjectId(recentProjectIds, firstProject._id);
      setRecentProjectIdsState(nextRecentProjectIds);
      setRecentProjectIdsInStorage(nextRecentProjectIds);
    }
    
    setIsInitialized(true);
  }, [isInitialized, isProjectsLoading, projects, recentProjectIds]);

  // Keep selected project valid when the available project list changes
  // (e.g. after deleting the currently selected project).
  useEffect(() => {
    if (!isInitialized || isProjectsLoading || !projects) return;

    if (projects.length === 0) {
      if (projectId !== null) {
        setProjectIdState(null);
        setLastProjectIdInStorage(null);
      }
      if (recentProjectIds.length > 0) {
        setRecentProjectIdsState([]);
        setRecentProjectIdsInStorage([]);
      }
      if (!isProjectIndependentPath(location.pathname)) {
        navigate({ to: "/", title: "Projects" });
      }
      return;
    }

    const validProjectIds = new Set(projects.map((row) => row._id));
    let nextRecentProjectIds = recentProjectIds.filter((id) => validProjectIds.has(id));

    if (projectId === null) {
      const fallbackProject = projects[0];
      if (fallbackProject) {
        setProjectIdState(fallbackProject._id);
        setLastProjectIdInStorage(fallbackProject._id);
        nextRecentProjectIds = addRecentProjectId(nextRecentProjectIds, fallbackProject._id);
      }
    } else if (!validProjectIds.has(projectId)) {
      const fallbackProject = projects[0];
      if (fallbackProject) {
        setProjectIdState(fallbackProject._id);
        setLastProjectIdInStorage(fallbackProject._id);
        nextRecentProjectIds = addRecentProjectId(nextRecentProjectIds, fallbackProject._id);
      }
    }

    const didRecentProjectsChange =
      nextRecentProjectIds.length !== recentProjectIds.length ||
      nextRecentProjectIds.some((id, index) => id !== recentProjectIds[index]);

    if (didRecentProjectsChange) {
      setRecentProjectIdsState(nextRecentProjectIds);
      setRecentProjectIdsInStorage(nextRecentProjectIds);
    }
  }, [
    isInitialized,
    isProjectsLoading,
    projects,
    projectId,
    recentProjectIds,
    location.pathname,
    navigate,
  ]);

  const setProjectId = useCallback((newProjectId: Id<"projects"> | null) => {
    setProjectIdState(newProjectId);
    setLastProjectIdInStorage(newProjectId);

    if (newProjectId) {
      setRecentProjectIdsState((currentProjectIds) => {
        const nextProjectIds = addRecentProjectId(currentProjectIds, newProjectId);
        setRecentProjectIdsInStorage(nextProjectIds);
        return nextProjectIds;
      });
    }
  }, []);

  const switchProject = useCallback(
    (newProjectId: Id<"projects">, navigateTo?: AppNavigateOptions) => {
      const currentPath = location.pathname;
      // Check if we're on a resource-specific page that might not belong to the new project.
      const resourcePages = [
        /^\/specPilot\/[^/]+$/, // /specPilot/$jobId
        /^\/revPilot\/[^/]+$/, // /revPilot/$comparisonId
        /^\/revCollab\/revisions\/[^/]+$/, // /revCollab/revisions/$revisionId
        /^\/project\/documents(?:\/[^/]+)?$/, // /project/documents or /project/documents/$documentId
      ];

      const isOnResourcePage = resourcePages.some((pattern) =>
        pattern.test(currentPath)
      );

      // Update project context
      setProjectId(newProjectId);

      if (navigateTo) {
        // Explicit navigation requested
        navigate(navigateTo);
      } else if (isOnResourcePage) {
        // If on a resource page, redirect to project page
        // This prevents showing data from wrong project
        navigate({ to: "/project", title: "Project" });
      }
      // Otherwise stay on current page (list pages will update their queries)
    },
    [location.pathname, navigate, setProjectId]
  );

  const value = useMemo(
    () => ({
      projectId,
      project: project || null,
      projects,
      recentProjectIds,
      isLoading: isProjectsLoading || isProjectLoading || !isInitialized,
      error: (projectsError || projectError) as Error | null,
      setProjectId,
      switchProject,
      refreshProjects,
    }),
    [
      projectId,
      project,
      projects,
      recentProjectIds,
      isProjectsLoading,
      isProjectLoading,
      isInitialized,
      projectsError,
      projectError,
      setProjectId,
      switchProject,
      refreshProjects,
    ]
  );

  return (
    <ProjectContext.Provider value={value}>{children}</ProjectContext.Provider>
  );
}

export function useProject(): ProjectContextValue {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error("useProject must be used within a ProjectProvider");
  }
  return context;
}
